import { useParams, useNavigate, useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { closeAllModals, modals } from '@mantine/modals';
import { useDisclosure } from '@mantine/hooks';
import UpdateChainStoreContractStoresModal from '../components/modal/chain-store-contracts/UpdateChainStoreContractStoresModal.tsx';
import {
  Paper,
  Stack,
  Card,
  LoadingOverlay,
  Title,
  Box,
  Group,
  Text,
  Button,
  SimpleGrid,
  Input,
  Alert,
  Divider,
} from '@mantine/core';
import {
  IconArrowRight,
  IconX,
  IconEdit,
  IconInfoCircle,
  IconStackPop,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { DataTable, DataTableColumn } from 'mantine-datatable';

import usePartner from '../hooks/partners/usePartner.ts';
import useChainStoreContract from '../hooks/chain-store-contracts/useChainStoreContract.ts';
import useUpdateChainStoreContract from '../hooks/chain-store-contracts/useUpdateChainStoreContract.ts';
import useDownloadChainStoreContractAttachment from '../hooks/chain-store-contracts/useDownloadChainStoreContractAttachment.ts';
import useBriefChainStores from '../hooks/partners/useBriefChainStores.ts';
import useUpdateChainStoreContractStores from '../hooks/chain-store-contracts/useUpdateChainStoreContractStores.ts';
import usePublishChainStoreContract from '../hooks/chain-store-contracts/usePublishChainStoreContract.ts';
import { formatDate } from '../utils/DateUtil.ts';

import type { Attachment } from '../models/attachment.ts';
import type {
  MutateChainStoreContractFormValues,
  MutateChainStoreContractFormInitialValues,
} from '../components/ui/MutateChainStoreContractForm.tsx';
import type { BriefChainStore } from '../models/chain-store-contract.ts';

import MutateChainStoreContractForm from '../components/ui/MutateChainStoreContractForm.tsx';
import PreviousPageButton from '../components/ui/PreviousPageButton.tsx';
import { RECEIPT_MODE_LABEL } from '../const/labels.ts';
import { notifySuccess } from '../utils/NotifySuccess.tsx';

export default function ChainStoreContractEditPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { partnerId: partnerId, id: id } = useParams();

  const { partner, isLoading: isPartnerLoading } = usePartner(partnerId);
  const { data: chainStoreContract, isLoading: isChainStoreContractLoading } =
    useChainStoreContract(partnerId, id);
  const { data: briefChainStores, isLoading: isBriefChainStoresLoading } =
    useBriefChainStores(partnerId);

  const {
    mutate: updateChainStoreContract,
    isPending: isUpdateChainStoreContractPending,
  } = useUpdateChainStoreContract();
  const {
    mutate: downloadChainStoreContractAttachment,
    isPending: isDownloadChainStoreContractAttachmentPending,
  } = useDownloadChainStoreContractAttachment();
  const {
    mutate: updateChainStoreContractStores,
    isPending: isUpdateChainStoreContractStoresPending,
  } = useUpdateChainStoreContractStores();
  const {
    mutate: publishChainStoreContract,
    isPending: isPublishChainStoreContractPending,
  } = usePublishChainStoreContract();

  const queryClient = useQueryClient();

  const [
    chainStoresModalOpened,
    { open: openChainStoresModal, close: closeChainStoresModal },
  ] = useDisclosure(false);

  // UI state
  const [isEditMode, setEditMode] = useState(false);
  const [isCreatedNoticeClosed, setCreatedNoticeClosed] =
    useState<boolean>(false);
  const [isSpecifyStoresWarningClosed, setSpecifyStoresWarningClosed] =
    useState<boolean>(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (
    isPartnerLoading ||
    isChainStoreContractLoading ||
    isBriefChainStoresLoading
  ) {
    return (
      <LoadingOverlay visible loaderProps={{ color: 'cyan', type: 'bars' }} />
    );
  }

  // 不是 DRAFT 階段的合約都不可編輯
  if (chainStoreContract && chainStoreContract.state !== 'DRAFT') {
    navigate(`/partners/${partnerId}/chain-store-contracts/${id}`);
  }

  // consts
  const shouldDisplayCreatedNotice: boolean =
    !!state?.created && isCreatedNoticeClosed;
  const isSpecifyStoresEmpty: boolean =
    chainStoreContract.profileType === 'SPECIFY' &&
    chainStoreContract.chainStores.length === 0;
  const shouldDisplaySpecifyStoresWarning: boolean =
    isSpecifyStoresEmpty && !isSpecifyStoresWarningClosed;

  const initialFormValues = (): MutateChainStoreContractFormInitialValues => {
    return {
      name: chainStoreContract.name,
      profileType: chainStoreContract.profileType,
      profitSharingMethod: chainStoreContract.profitSharingMethod,
      profitSharingValue:
        chainStoreContract.profitSharingMethod === 'FIXED'
          ? chainStoreContract.basicFixedFee
          : chainStoreContract.basicPercentage,
      startAt: chainStoreContract.startAt,
      endAt: chainStoreContract.endAt,
      chainStoreNotificationEndpoint:
        chainStoreContract.chainStoreNotificationEndpoint,
      requestAmountLimit: chainStoreContract.requestAmountLimit,
      receiptMode: chainStoreContract.receiptMode,
      existingAttachments: chainStoreContract.attachments,
    };
  };

  const chainStoreColumns: DataTableColumn<BriefChainStore>[] = [
    {
      title: '場站名稱',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'name',
    },
  ];

  // callback functions
  function onSubmit(values: MutateChainStoreContractFormValues) {
    if (isUpdateChainStoreContractPending) return;

    const updateRequest = {
      id: chainStoreContract.id,
      partnerId: partnerId,
      name: values.name,
      profileType: values.profileType,
      profitSharingMethod: values.profitSharingMethod,
      basicFixedFee:
        values.profitSharingMethod === 'FIXED'
          ? values.profitSharingValue
          : '0.0',
      basicPercentage:
        values.profitSharingMethod === 'PERCENTAGE'
          ? values.profitSharingValue
          : '0.0',
      startAt: values.startAt,
      endAt: values.endAt,
      chainStoreNotificationEndpoint: values.chainStoreNotificationEndpoint,
      receiptMode: values.receiptMode,
    };

    const uploadAttachmentRequest = values.newAttachments.map((attachment) => {
      return {
        partnerId: partnerId,
        contractId: chainStoreContract.id,
        file: attachment.file,
      };
    })[0];

    const deleteAttachmentRequest = values.deletedAttachments.map(
      (attachment) => {
        return {
          partnerId: partnerId,
          contractId: chainStoreContract.id,
          attachmentId: attachment.id,
        };
      },
    )[0];

    updateChainStoreContract(
      [updateRequest, uploadAttachmentRequest, deleteAttachmentRequest],
      {
        onSuccess: () => {
          notifySuccess('編輯連鎖店合約成功');
          queryClient.invalidateQueries({
            queryKey: [
              'partner',
              partnerId,
              'chainStoreContract',
              chainStoreContract.id,
            ],
          });
          setEditMode(false);
        },
        onError: () => {
          notifications.show({
            title: '失敗',
            message: '',
            color: 'red',
            icon: <IconX />,
          });
        },
      },
    );
  }

  function onDownloadAttachment(attachment: Attachment) {
    if (isDownloadChainStoreContractAttachmentPending) return;

    downloadChainStoreContractAttachment(
      {
        partnerId: partnerId,
        contractId: chainStoreContract.id,
        attachmentId: attachment.id,
      },
      {
        onSuccess: (downloadUrl) => {
          // 透過動態建立 <a> 標籤來下載
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = attachment.displayName; // 不設定時，會使用伺服器回傳的檔名
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          notifySuccess('下載檔案成功');
        },
      },
    );
  }

  function onClickEditChainStoresButton() {
    openChainStoresModal();
  }

  function handleChainStoresConfirm(formValues) {
    updateChainStoreContractStores(
      { partnerId, contractId: chainStoreContract.id, ...formValues },
      {
        onSuccess: () => {
          closeChainStoresModal();
          notifySuccess('更新指定場站成功');
          queryClient.invalidateQueries({
            queryKey: [
              'partner',
              partnerId,
              'chainStoreContract',
              chainStoreContract.id,
            ],
          });
        },
        onError: () => {
          closeChainStoresModal();
          queryClient.invalidateQueries({
            queryKey: [
              'partner',
              partnerId,
              'chainStoreContract',
              chainStoreContract.id,
            ],
          });
        },
      },
    );
  }

  function onClickPublishButton() {
    const modalId = modals.openContextModal({
      modal: 'confirmModal',
      title: <Text fw={700}>發佈合約</Text>,
      closeOnEscape: true,
      closeOnClickOutside: true,
      innerProps: {
        modalBody: `是否確認發佈此合約？`,
        loading: false,
        onConfirm: () => {
          modals.updateContextModal({
            modalId,
            innerProps: {
              loading: true,
            },
          });

          publishChainStoreContract(
            { partnerId, id },
            {
              onSuccess: () => {
                closeAllModals();
                // 轉導至 detailPage
                navigate(`/partners/${partnerId}/chain-store-contracts/${id}`, {
                  state: { published: true },
                });
              },
              onSettled: () => {
                modals.updateContextModal({
                  modalId,
                  innerProps: {
                    loading: false,
                  },
                });
              },
            },
          );
        },
      },
    });
  }

  return (
    <>
      {chainStoresModalOpened && chainStoreContract && (
        <UpdateChainStoreContractStoresModal
          opened={chainStoresModalOpened}
          onClose={closeChainStoresModal}
          specifiedChainStores={chainStoreContract.chainStores}
          chainStoreData={briefChainStores}
          isPending={isUpdateChainStoreContractStoresPending}
          onConfirm={handleChainStoresConfirm}
        />
      )}
      <Box mb={70}>
        <PreviousPageButton
          href={`/partners/${partnerId}`}
          content={'返回業者詳情頁'}
        />

        {shouldDisplayCreatedNotice && (
          <Alert
            variant="light"
            color="green"
            mb="md"
            icon={<IconInfoCircle />}
            withCloseButton
            onClose={() => setCreatedNoticeClosed(true)}
            title="成功新增合約"
          />
        )}

        {shouldDisplaySpecifyStoresWarning && (
          <Alert
            variant="light"
            color="yellow"
            mb="md"
            icon={<IconInfoCircle />}
            withCloseButton
            onClose={() => setSpecifyStoresWarningClosed(true)}
            title="請先完成指定場站設定，才能發佈合約"
          />
        )}

        <Stack gap="lg">
          {isEditMode ? (
            <>
              <Paper radius="lg" p="xl" shadow="xs">
                <Group justify="space-between">
                  <Title order={3}>{`${partner.name} | 連鎖店合約`}</Title>
                  <Group>
                    <Button
                      variant="outline"
                      onClick={() => setEditMode(false)}
                      fw={400}
                    >
                      取消
                    </Button>
                    <Button
                      type="submit"
                      form="mutateChainStoreContractForm"
                      loading={isUpdateChainStoreContractPending}
                      fw={400}
                    >
                      儲存
                    </Button>
                  </Group>
                </Group>

                <MutateChainStoreContractForm
                  initialValues={initialFormValues()}
                  onSubmit={onSubmit}
                />
              </Paper>
            </>
          ) : (
            <>
              <Paper radius="lg" p="xl" shadow="xs">
                <Group justify="space-between">
                  <Title order={3}>{`${partner.name} | 連鎖店合約`}</Title>
                  <Group>
                    <Button
                      leftSection={<IconEdit size={20} />}
                      variant="outline"
                      onClick={() => setEditMode(true)}
                      fw={400}
                    >
                      編輯
                    </Button>
                  </Group>
                </Group>

                <Text size="sm" fw={500} c="gray" mt={40}>
                  基本設定
                </Text>
                <Divider mt={10} mb={20} />
                <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl" mb={60}>
                  <Input.Wrapper withAsterisk label="合約名稱">
                    <Text>{chainStoreContract.name}</Text>
                  </Input.Wrapper>
                  <Input.Wrapper label="合約套用方式">
                    <Text>
                      {chainStoreContract.profileType === 'DEFAULT'
                        ? '預設合約'
                        : '指定場站合約'}
                    </Text>
                  </Input.Wrapper>
                  <Input.Wrapper withAsterisk label="合約生效起迄日">
                    <Group>
                      <Text>{formatDate(chainStoreContract.startAt)}</Text>
                      <IconArrowRight size={20} />
                      <Text>{formatDate(chainStoreContract.endAt)}</Text>
                    </Group>
                  </Input.Wrapper>
                  <Input.Wrapper label="連鎖店交易通知網址">
                    <Text>
                      {!chainStoreContract.chainStoreNotificationEndpoint
                        ? '-'
                        : chainStoreContract.chainStoreNotificationEndpoint}
                    </Text>
                  </Input.Wrapper>
                  <Input.Wrapper label="發票開立方式">
                    <Text>
                      {RECEIPT_MODE_LABEL[chainStoreContract.receiptMode]}
                    </Text>
                  </Input.Wrapper>
                  <Input.Wrapper label="請款金額上限">
                    <Text>{chainStoreContract.requestAmountLimit || '-'}</Text>
                  </Input.Wrapper>
                </SimpleGrid>

                <Text size="sm" fw={500} c="gray" mt={60}>
                  分潤方式
                </Text>
                <Divider mt={10} mb={20} />
                <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl" mb={60}>
                  <Input.Wrapper withAsterisk label="分潤條件">
                    <Text>
                      {chainStoreContract.profitSharingMethod === 'FIXED'
                        ? `固定費用 ${chainStoreContract.basicFixedFee} 元`
                        : `依交易金額百分比 ${chainStoreContract.basicPercentage} %`}
                    </Text>
                  </Input.Wrapper>
                </SimpleGrid>

                <Text size="sm" fw={500} c="gray" mt={60} mb={20}>
                  合約上傳
                </Text>
                <Card
                  p="xl"
                  shadow="none"
                  style={{ border: '2px dashed var(--mantine-color-gray-2 )' }}
                >
                  <Text size="sm" fw={500} inline mb={10}>
                    已選擇檔案：
                  </Text>
                  {chainStoreContract.attachments.map((attachment) => {
                    return (
                      <Group justify="space-between">
                        <Text size="sm" fw={500}>
                          {attachment.displayName}
                        </Text>
                        <Button
                          variant="transparent"
                          onClick={() => onDownloadAttachment(attachment)}
                        >
                          下載檔案
                        </Button>
                      </Group>
                    );
                  })}
                </Card>
              </Paper>

              {chainStoreContract.profileType === 'SPECIFY' && (
                <Paper radius="lg" p="xl" shadow="xs" mt={20} mb={40}>
                  <Group justify="space-between">
                    <Title order={3}>合約指定場站</Title>
                    <Button
                      leftSection={<IconEdit size={20} />}
                      variant="outline"
                      onClick={onClickEditChainStoresButton}
                      disabled={chainStoreContract.profileType !== 'SPECIFY'}
                      fw={400}
                    >
                      編輯
                    </Button>
                  </Group>
                  <DataTable
                    idAccessor="id"
                    highlightOnHover
                    minHeight={150}
                    columns={chainStoreColumns}
                    records={chainStoreContract.chainStores}
                    verticalSpacing="sm"
                    noRecordsText="目前還沒有任何指定場站，點擊「編輯」開始建立！"
                    fetching={isChainStoreContractLoading}
                  />
                </Paper>
              )}
            </>
          )}
        </Stack>
      </Box>

      <Box
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        h={70}
        p="md"
        bg="var(--mantine-color-white)"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          zIndex: 100,
        }}
      >
        <Group justify="flex-end">
          <Button
            onClick={onClickPublishButton}
            loading={isPublishChainStoreContractPending}
            disabled={isSpecifyStoresEmpty || isEditMode}
            fw={400}
            leftSection={<IconStackPop />}
          >
            發佈
          </Button>
        </Group>
      </Box>
    </>
  );
}
